import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from 'tea-component';
import { connect, useHistory, useLocation } from 'umi';
import ContentBody from '@op/components/ContentBody';
import useLanguage from '@op/hooks/useLanguage';
import { getStatsDetailParams } from '@op/utils/storageUtils';
import { useDrilldownParams } from '@op/hooks/useDrilldownParams';
import TopicKeywordSection from './TopicKeywordSection';
import SteamGameStoreSection from './SteamGameStoreSection';
import CommentTableSection from './CommentTableSection';
import './index.scss';
import CardLoading from '@op/components/CardLoading';
import CacheExpiredTips from '@op/components/CacheExpiredTips';

function VersionAnalysisDetails({ gameInfo, wholeGameInfo }: any) {
  const history = useHistory();
  const location = useLocation();
  const { formatMessage, isEn } = useLanguage();
  const { readParams } = useDrilldownParams();
  /** 参数读取失败（缓存失效）时展示兜底提示 */
  const [invalid, setInvalid] = useState(false);

  const [filter, setFilter] = useState<any>(null);
  const [title, setTitle] = useState<string>('Version Analysis Details');

  const [chartClickFilter, setChartClickFilter] = useState<{
    time?: string;
    sentiment?: 'positive' | 'negative';
  } | null>(null);

  useEffect(() => {
    async function loadParams() {
      // 方案 1：从服务端/本地存储读取（由 Hook 内部处理）
      let pageParams: any = await readParams();

      // 方案 2：原有 sessionStorage 逻辑（兼容无 _s 标记的旧 URL）
      if (!pageParams) {
        pageParams = getStatsDetailParams();
      }

      if (pageParams) {
        const { filter, type, record } = pageParams || {};
        setFilter(filter);
        if (type) {
          setTitle(record?.[type] || 'Version Analysis Details');
        } else {
          const { update_title_en: titleEn, update_title_cn: titleCn } = record || {};
          if (titleEn || titleCn) {
            setTitle(isEn ? titleEn : titleCn || titleEn);
          }
        }
      } else {
        setInvalid(true);
      }
    }
    loadParams();
  }, [location.search, isEn]);

  const handleChartClick = useCallback((data: { time: string; sentiment?: 'positive' | 'negative'; }) => {
    setChartClickFilter(data);
  }, []);

  const handleClearChartFilter = useCallback(() => {
    setChartClickFilter(null);
  }, []);

  // 缓存失效：展示统一兜底提示（含「返回首页」按钮跳 /opinion）
  if (invalid) {
    return (
      <Layout.Content className="dbo-version-analysis-details">
        <ContentBody>
          <CacheExpiredTips scene="drilldown" />
        </ContentBody>
      </Layout.Content>
    );
  }

  return (
    <Layout.Content className="dbo-version-analysis-details">
      <ContentBody
        contentHeard={
          <Layout.Content.Header
            showBackButton
            onBackButtonClick={() => history.goBack()}
            title={formatMessage({ id: title })}
          />
        }
      >
        <CardLoading loading={!filter} wrapperStyle={{ marginTop: 0 }}>
          <TopicKeywordSection filter={filter} gameInfo={gameInfo} />
          <SteamGameStoreSection
            filter={filter}
            onChartClick={handleChartClick}
          />
          <CommentTableSection
            filter={filter}
            gameInfo={gameInfo}
            wholeGameInfo={wholeGameInfo}
            chartClickFilter={chartClickFilter}
            onClearChartFilter={handleClearChartFilter}
          />
        </CardLoading>
      </ContentBody>
    </Layout.Content>
  );
}

export default connect((state: any) => ({
  gameInfo: state.opinionCommon.gameInfo,
  wholeGameInfo: state.opinionCommon.wholeGameInfo,
}))(VersionAnalysisDetails);
